<template>
  <div
    v-if="isPageTranslated && isPageTranslated.length > 0"
    ref="target"
    :class="{
      'language-selector': true,
      hover: isGlobeClicked,
    }"
    @click="toggleLangSelector"
  >
    <ul>
      <svg
        :class="{
          'block md:hidden': true,
        }"
        viewBox="0 0 385.44 385.44"
        xmlns="http://www.w3.org/2000/svg"
        width="12"
      >
        <path
          xmlns="http://www.w3.org/2000/svg"
          d="M193,7.5c102.09.17,184.91,83.09,184.91,185.22S295,377.94,192.72,377.94,7.5,295,7.5,192.72,90.34,7.67,192.43,7.5Zm57,247H135.41c9.86,65.85,34.61,108,57.31,108s47.46-42.2,57.33-108m100.86,0H265.67c-6.58,46.07-20.39,83.38-38.36,104.51a170.17,170.17,0,0,0,123.6-104.51m-231.12,0H34.53A170.21,170.21,0,0,0,158.15,359c-18-21.13-31.78-58.44-38.36-104.51m-1.9-108H29.36a170.41,170.41,0,0,0,0,92.61h88.53a460.31,460.31,0,0,1,0-92.61m134.15,0H133.42a441.89,441.89,0,0,0,0,92.61H252a442.62,442.62,0,0,0,0-92.61m104.06,0H267.56a456.48,456.48,0,0,1,0,92.61H356.1a170.4,170.4,0,0,0,0-92.61m-198-119.95A170.21,170.21,0,0,0,34.53,131h85.26c6.58-46.07,20.39-83.4,38.36-104.51M250.05,131c-9.87-65.85-34.61-108-57.33-108s-47.45,42.19-57.31,108ZM227.31,26.47c18,21.11,31.78,58.44,38.36,104.51h85.24A170.17,170.17,0,0,0,227.31,26.47"
          stroke-miterlimit="10"
          stroke-width="15"
          fill-rule="evenodd"
        />
      </svg>
      <li>
        <svg
          :class="{
            '-ml-5 mr-2 hidden md:block': true,
          }"
          viewBox="0 0 385.44 385.44"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
        >
          <path
            xmlns="http://www.w3.org/2000/svg"
            d="M193,7.5c102.09.17,184.91,83.09,184.91,185.22S295,377.94,192.72,377.94,7.5,295,7.5,192.72,90.34,7.67,192.43,7.5Zm57,247H135.41c9.86,65.85,34.61,108,57.31,108s47.46-42.2,57.33-108m100.86,0H265.67c-6.58,46.07-20.39,83.38-38.36,104.51a170.17,170.17,0,0,0,123.6-104.51m-231.12,0H34.53A170.21,170.21,0,0,0,158.15,359c-18-21.13-31.78-58.44-38.36-104.51m-1.9-108H29.36a170.41,170.41,0,0,0,0,92.61h88.53a460.31,460.31,0,0,1,0-92.61m134.15,0H133.42a441.89,441.89,0,0,0,0,92.61H252a442.62,442.62,0,0,0,0-92.61m104.06,0H267.56a456.48,456.48,0,0,1,0,92.61H356.1a170.4,170.4,0,0,0,0-92.61m-198-119.95A170.21,170.21,0,0,0,34.53,131h85.26c6.58-46.07,20.39-83.4,38.36-104.51M250.05,131c-9.87-65.85-34.61-108-57.33-108s-47.45,42.19-57.31,108ZM227.31,26.47c18,21.11,31.78,58.44,38.36,104.51h85.24A170.17,170.17,0,0,0,227.31,26.47"
            stroke-miterlimit="10"
            stroke-width="15"
            fill-rule="evenodd"
          />
        </svg>
        <a :data-language="currLang.code">{{ currLang.label }}</a>
      </li>

      <li
        v-for="lang in languagesToShow.filter((val) => val.code !== currLang.code)"
        v-show="isGlobeClicked"
        :key="lang.code"
        :class="{
          flex: isGlobeClicked,
          'hover:bg-gray-200': true,
          'cursor-pointer': true,
        }"
        @click="() => changeSiteLang(lang.code)"
      >
        <a :data-language="lang.code">
          {{ lang.label }}
        </a>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core';
import type { TranslatedPathnameDocsQueryResult } from '~/sanity/types';
import { translatedPathnameDocsQuery } from '../../../queries/components/Molecules/TranslationPathnames.query';
import {
  ALL_LANGUAGES,
  getAvailableLanguagesForPath,
  replaceLanguageInPath,
  replaceLanguageInUrl,
} from '~/utils/language-translation';

const isGlobeClicked = ref(false);
const target = ref<HTMLElement | null>(null);

onClickOutside(target, () => {
  isGlobeClicked.value = false;
});

const allLanguages = ALL_LANGUAGES;

const currPath = useRoute().path;

const { data: translationPathnameDocs } =
  await useWwwSanityQueryNonNull<TranslatedPathnameDocsQueryResult>(
    translatedPathnameDocsQuery,
    {},
    { wwwHint: 'lang-ops' }
  );

const currLang = computed(() => {
  const defLang = allLanguages.find((lang) => lang.code === 'en')!;
  const langMatch = currPath.match(/^\/([a-z]{2})\//);
  if (!langMatch) {
    return defLang; // default to 'en' if no language prefix is found
  }

  const found = allLanguages.find((lang) => lang.code === langMatch[1]) || defLang;
  return found;
});

const isPageTranslated = computed(() => {
  const docs = translationPathnameDocs.value;
  if (!docs) return [];

  // Use shared utility function
  return getAvailableLanguagesForPath(currPath, docs);
});

const languagesToShow = allLanguages.filter(
  (lang) => lang.code === 'en' || isPageTranslated.value.includes(lang.code)
);
const baseUrl = process.env.NUXT_BASE_URL || 'https://www.onshape.com';
const route = useRoute();
const fullUrl = new URL(route.fullPath, baseUrl).toString();
const links = languagesToShow.map((lang) => {
  return {
    rel: 'alternate' as const,
    hreflang: lang.code,
    href: replaceLanguageInUrl(fullUrl, lang.code, { insertWhenMissing: true }),
  };
});
useHead({
  link: [...links],
});
const toggleLangSelector = () => {
  if (window.innerWidth > 767) {
    isGlobeClicked.value = !isGlobeClicked.value;
  }
};

const changeSiteLang = (selected: string) => {
  // Use shared utility function
  const newPath = replaceLanguageInPath(currPath, selected);
  document.location = newPath;
  isGlobeClicked.value = false;
};
</script>

<style lang="scss" scoped>
@media (min-width: 768px) {
  .language-selector {
    height: 100%;
    &.hover {
      ul {
        background-color: var(--www-color-white);
        &:hover {
          li:first-of-type {
            svg {
              fill: var(--www-color-neutral-medium-dark) !important;
            }
            a {
              color: var(--www-color-neutral-medium-dark);
            }
          }
        }
        li {
          &:first-of-type {
            &::before {
              border-bottom: 2px solid var(--www-color-primary-lighter);
              content: '';
              display: block;
              left: 20%;
              position: absolute;
              top: 34px;
              width: 60%;
              z-index: 11;
            }
          }
          svg {
            fill: var(--www-color-neutral-medium-dark) !important;
          }
          a {
            color: var(--www-color-neutral-medium-dark);
          }
        }
      }
    }
    ul {
      background-color: transparent;
      width: 77px;
      text-align: center;
      position: relative;
      cursor: pointer;
      &:hover {
        li:first-of-type {
          svg {
            fill: var(--www-color-primary-lighter) !important;
          }
          a {
            color: var(--www-color-primary-lighter);
          }
        }
      }
      li {
        padding: 10px 0 10px 0;
        display: flex;
        justify-content: center;
        align-items: center;
        &:hover {
          background-color: #ebebee;
        }
        &:first-of-type {
          &:hover {
            background-color: transparent;
          }
          svg {
            fill: var(--www-color-white);
          }
        }
        img {
          display: none !important;
        }
        a {
          display: block;
          font-size: 10px;
          font-weight: 700;
          text-decoration: none !important;
          text-transform: uppercase;
          color: var(--www-color-white);
        }
      }
    }
  }
}
@media (max-width: 767px) {
  .language-selector {
    ul {
      border-top: 1px solid #5d696a;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      margin-left: 0;
      padding: 10px 15px;
      border-bottom: 1px solid #5d696a;
      margin-bottom: 20px;

      svg {
        fill: var(--www-color-white) !important;
      }
    }

    li {
      display: block !important;
      a {
        font-weight: bold;
      }
    }
  }
}
</style>
